import React from 'react';
import Button from '../../components/Button/Button';
import { Subscription, TierPlan } from './PaymentManager'; // Assuming interfaces are exported

interface CurrentSubscriptionDisplayProps {
  subscription: Subscription;
  tierPlans: TierPlan[];
  billingInterval: 'month' | 'year';
  onManageSubscription: () => void;
  isCreatingPortalSession: boolean;
  subscriptionError: string | null;
  portalError: string | null;
  startFade: boolean; // Added for fade-in effect
}

const CurrentSubscriptionDisplay: React.FC<CurrentSubscriptionDisplayProps> = ({
  subscription,
  tierPlans,
  billingInterval,
  onManageSubscription,
  isCreatingPortalSession,
  subscriptionError,
  portalError,
  startFade
}) => {
  // Find the corresponding tier plan based on the current subscription's product name
  // This might need refinement if product_name doesn't exactly match tierName
  const planProduct = tierPlans.find(
    (p) => p.tierName.toLowerCase() === subscription.product_name?.toLowerCase()
  );
  // Display based on interval if available, fallback logic might be needed
  const displayPrice = billingInterval === 'year' ? planProduct?.yearlyDisplayPrice : planProduct?.monthlyDisplayPrice;

  const subscriptionStatusLabel = 
    (subscription.status === 'active' && subscription.cancel_at_period_end)
    ? 'Cancelled'
    : (subscription.status || 'Status');

  const renewalLabel = 
    (subscription.status === 'active' && !subscription.cancel_at_period_end) 
    ? 'Renews on' 
    : 'Expires on';

  return (
    // Apply fade-in directly to the wrapper holding the details
    <div className={`subscription-details-wrapper fade-in-content ${startFade ? 'visible' : ''}`}>
      {/* Display subscription/portal errors */}
      {subscriptionError && <p className="subscription-error">{subscriptionError}</p>}
      {portalError && <p className="subscription-error">{portalError}</p>}
      {/* Two-column container */}
      <div className="subscription-columns-container">
        {/* Left Column */}
        <div className="subscription-left-column">
          {/* Main plan information */} 
          <div className="subscription-plan-header">
            <div className="plan-info">
              <div className="subscription-plan-label">{
                subscriptionStatusLabel
              }</div>
              <div className="subscription-plan-name">{subscription.product_name || 'N/A'}</div>
            </div>
          </div>
          {/* Manage Subscription Button */} 
          <div className="subscription-actions" style={{ marginTop: '16px' }}>
            <Button
              variant="secondary"
              size="small"
              onClick={onManageSubscription}
              isLoading={isCreatingPortalSession}
              disabled={isCreatingPortalSession}
            >
              Manage Subscription
            </Button>
          </div>
        </div>
        {/* Right Column */}
        <div className="subscription-right-column">
          {/* Details table */} 
          <div className="subscription-details-table">
            {/* Price */}
            {displayPrice && (
              <div className="subscription-kv-row">
                <span className="sub-label">Price</span>
                <span className="sub-value">{displayPrice}</span>
              </div>
            )}

            {/* Seats */} 
            {typeof subscription.seats !== 'undefined' && (
              <div className="subscription-kv-row">
                <span className="sub-label">Seats</span>
                <span className="sub-value">{subscription.seats}</span>
              </div>
            )}

            {/* Renewal/Expiry Date */} 
            {subscription.renewal_date && (
              <div className="subscription-kv-row">
                <span className="sub-label">{renewalLabel}</span>
                <span className="sub-value">{new Date(subscription.renewal_date).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentSubscriptionDisplay; 